/**
 * Sprint 11+12 — Theme hook.
 *
 * Single source of truth for the active theme. The user's preference is
 * persisted by `useUIStore`; this hook resolves "system" against the OS via
 * `prefers-color-scheme` and toggles the `dark` class on <html> so CSS
 * variables in tokens.css flip atomically.
 */
import { useEffect, useMemo, useCallback } from 'react'
import { useUIStore, type ThemePreference } from '@/stores/ui'

const QUERY = '(prefers-color-scheme: dark)'

/** Read the OS preference. SSR-safe: defaults to false. */
export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(QUERY).matches
}

/** Apply the resolved theme to <html> by toggling the `dark` class. */
export function applyTheme(isDark: boolean) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (isDark) root.classList.add('dark')
  else root.classList.remove('dark')
  // Hint to the UA so form controls / scrollbars match.
  root.style.colorScheme = isDark ? 'dark' : 'light'
}

export function resolveIsDark(theme: ThemePreference, prefersDark: boolean): boolean {
  if (theme === 'dark') return true
  if (theme === 'light') return false
  return prefersDark
}

/**
 * useTheme — primary consumer hook.
 * Returns the saved preference, the resolved boolean, a setter, and a cycle
 * helper for the topbar toggle (light → dark → system → light).
 */
export function useTheme() {
  const theme = useUIStore((s) => s.theme)
  const setThemePref = useUIStore((s) => s.setTheme)

  const isDark = useMemo(() => resolveIsDark(theme, getSystemPrefersDark()), [theme])

  // Apply on mount + whenever preference changes.
  useEffect(() => {
    applyTheme(resolveIsDark(theme, getSystemPrefersDark()))
  }, [theme])

  // React to OS-level changes only when the user picked "system".
  useEffect(() => {
    if (theme !== 'system') return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(QUERY)
    const handler = (e: MediaQueryListEvent) => applyTheme(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback((next: ThemePreference) => setThemePref(next), [setThemePref])
  const cycleTheme = useCallback(() => {
    const next: ThemePreference = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
    setThemePref(next)
  }, [theme, setThemePref])

  return { theme, isDark, setTheme, cycleTheme }
}

/**
 * Convenience hook used by chart components — returns the colour set the
 * recharts axes/grid/tooltip should use today.
 */
export function useChartTheme() {
  const { isDark } = useTheme()
  return {
    isDark,
    grid:        isDark ? '#334155' : '#f1f5f9',
    axisTick:    isDark ? '#64748b' : '#94a3b8',
    axisLine:    isDark ? '#334155' : '#e2e8f0',
    tooltipBg:   isDark ? '#1e293b' : '#ffffff',
    tooltipBorder: isDark ? '#334155' : '#e2e8f0',
    tooltipText: isDark ? '#f1f5f9' : '#0f172a',
  }
}
